import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

class MapErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[MapErrorBoundary] Google Maps component error:', error.message, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      const message = this.state.error?.message ?? 'Unknown error';

      return (
        <div className="z-10 absolute top-3 left-1/2 -translate-x-1/2 bg-red100 border border-red700 rounded-lg px-4 py-3 max-w-lg shadow-sm">
          <p className="text-red700 font-medium text-xs mb-1">Google Maps API — third-party error</p>
          <p className="text-black700 text-xs font-mono break-all">{message}</p>
          <p className="text-grey700 text-xs mt-2">
            Some map features are unavailable due to a Google Maps API configuration issue. The map is running in
            limited mode. Please verify the Google Maps API key, billing status, and enabled APIs in Google Cloud
            Console.
          </p>
        </div>
      );
    }

    return this.props.children;
  }
}

export default MapErrorBoundary;
